// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import { createSelector } from 'reselect';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import {
  selectDomainCreateProducts,
  selectProductsByType,
  unsetProductsWorkingStatus,
  type DecoratedProduct,
} from '@gandi/cart';
import type { SSEPollerClient } from '@gandi/sse-poller/client';
import { Base } from '@gandi/ui';

import { useAppSelector } from 'src/hooks';

import { fetchIDNAvailableLanguages as fetchIDNLanguages } from '../../../DomainCreate/modules/actions/idn';
import { select as selectIDNLanguages } from '../../../DomainCreate/modules/selectors/idn';

import type { FullState } from '../../../types';
import {
  fetchDomainCreateProductsCatalog,
  fetchClaims,
  fetchDomainRenewProductsCatalog,
} from '../../modules/actions';

import { ProductKind } from './ProductKind';
import { selectHasDomainWithIdn } from './modules/selectors';
import {
  selectHasDomainCreateIDNErrors,
  selectProductKinds,
} from './selectors';

const productTypeProps = { productType: 'domain' };

const selectDomainRenewProducts: (state: FullState) => Array<DecoratedProduct> =
  createSelector(
    [state => selectProductsByType(state, productTypeProps)],
    domainProducts =>
      domainProducts.filter(product => product.process === 'renew'),
  );

export function Products(): React.MixedElement {
  const productKinds = useAppSelector(selectProductKinds);
  const domainRenewProducts = useAppSelector(selectDomainRenewProducts);
  const domainCreateProducts = useAppSelector(selectDomainCreateProducts);
  const authenticated = useAppSelector(selectIsAuthenticatedUser);
  const IDNLanguages = useAppSelector(selectIDNLanguages);
  const IDNErrors = useAppSelector(selectHasDomainCreateIDNErrors);
  const hasDomainWithIdn = useAppSelector(selectHasDomainWithIdn);

  const dispatch = useDispatch();

  const ssePollers: Array<SSEPollerClient> = [];

  /// componentDidMount
  React.useEffect(() => {
    if (!IDNLanguages.length && (IDNErrors || hasDomainWithIdn)) {
      dispatch(fetchIDNLanguages());
    }

    if (domainCreateProducts.length) {
      ssePollers.push(
        // $FlowFixMe[incompatible-call]
        dispatch(fetchDomainCreateProductsCatalog(domainCreateProducts)),
      );

      // fetch claims only if authenticated
      if (authenticated) {
        // $FlowFixMe[incompatible-call]
        ssePollers.push(dispatch(fetchClaims(domainCreateProducts)));
      }
    }

    if (domainRenewProducts.length) {
      ssePollers.push(
        // $FlowFixMe[incompatible-call]
        dispatch(fetchDomainRenewProductsCatalog(domainRenewProducts)),
      );
    }

    // componentDidUnmount
    return () => {
      ssePollers.forEach(ssePoller => ssePoller.close());
      dispatch(unsetProductsWorkingStatus());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Base tagName="ul" marginTop="none" marginBottom="base" padding="none">
      {productKinds.map(kind => (
        <ProductKind key={kind} kind={kind} />
      ))}
    </Base>
  );
}
